import { ContentPageVariant } from '@pnpmworkspace/shared-types';
import { Appointment } from '../common/appointment';
import { Banner } from '../common/banner';
import { Intro } from '../common/intro';
import {
  ContentPageSectionVisibility,
  PlannerConfigEntityPhraseStrings,
} from '@/services/configurator/PlannerConfigTypes';
import Text from '@ingka/text';
import { renderPhraseString } from '../../util';
import VideoPlayer from '@ingka/video-player';

export const BannersWithVideoAndExternalLink = ({
  plannerUrl,
  imageUrl,
  phraseStrings,
  secondaryBannerImgUrl,
  contentPageVariant,
  dynamicView,
  videoUrl,
}: {
  plannerUrl?: string;
  imageUrl?: string;
  phraseStrings?: PlannerConfigEntityPhraseStrings;
  secondaryBannerImgUrl?: string;
  contentPageVariant?: ContentPageVariant;
  dynamicView?: ContentPageSectionVisibility;
  videoUrl?: string;
}) => {
  return (
    <div className='flex flex-wrap gap-8'>
      <Intro
        title={renderPhraseString('page-intro-title', phraseStrings)}
        description={renderPhraseString(
          'page-intro-description',
          phraseStrings,
        )}
      />
      <Banner
        title={
          renderPhraseString('page-primary-banner-title', phraseStrings) ??
          renderPhraseString('title', phraseStrings) ??
          renderPhraseString('planner.fallback-title', phraseStrings)
        }
        description={renderPhraseString(
          'page-primary-banner-description',
          phraseStrings,
        )}
        cta={
          renderPhraseString('page-primary-banner-cta', phraseStrings) ??
          renderPhraseString('cta', phraseStrings) ??
          renderPhraseString('planner.fallback.cta', phraseStrings)
        }
        image={imageUrl}
        alt={renderPhraseString('page-primary-banner-alt-text', phraseStrings)}
        linkTo={plannerUrl || ''}
        loading='eager'
        componentType='planner_banner'
        testId='plann-primary-banner'
        contentPageVariant={contentPageVariant}
      />
      <Appointment
        dynamicView={dynamicView!}
        appointment1={{
          title: renderPhraseString('page-primary-card-title', phraseStrings),
          description: renderPhraseString(
            'page-primary-card-description',
            phraseStrings,
          ),
          bookButton: renderPhraseString(
            'page-primary-card-cta',
            phraseStrings,
          ),
          bookUrl: 'page-primary-card-cta-url',
        }}
        appointment2={{
          title: renderPhraseString('page-secondary-card-title', phraseStrings),
          description: renderPhraseString(
            'page-secondary-card-description',
            phraseStrings,
          ),
          bookButton: renderPhraseString(
            'page-secondary-card-cta',
            phraseStrings,
          ),
          bookUrl: 'page-secondary-card-cta-url',
        }}
        contentPageVariant={contentPageVariant}
      />
      {dynamicView && dynamicView['video'] && (
        <Video
          videoUrl={videoUrl}
          videoDescription={renderPhraseString(
            'videoDescription',
            phraseStrings,
          )}
          videoTitle={renderPhraseString('videoTitle', phraseStrings)}
        />
      )}
      {/* todo: make this type clearer; this is actually okay because the data was kebab-cased even though this isn't reflected in the type */}
      {dynamicView && dynamicView.pageSecondaryBanner && (
        <Banner
          title={renderPhraseString(
            'page-secondary-banner-title',
            phraseStrings,
          )}
          description={renderPhraseString(
            'page-secondary-banner-description',
            phraseStrings,
          )}
          cta={renderPhraseString('page-secondary-banner-cta', phraseStrings)}
          image={secondaryBannerImgUrl}
          alt={renderPhraseString(
            'page-secondary-banner-img-alt-text',
            phraseStrings,
          )}
          // remove or come from marketData
          linkTo='testLink'
          imagePosition='trailing'
          componentType='steps_banner'
          contentPageVariant={contentPageVariant}
        />
      )}
    </div>
  );
};

export const Video = ({
  videoUrl,
  videoDescription,
  videoTitle,
}: {
  videoUrl?: string;
  videoDescription: string;
  videoTitle: string;
}) => {
  return (
    <>
      {videoUrl && videoDescription && videoTitle && (
        <>
          <Text tagName='h2' textStyle='Heading.M' className='mb-4'>
            {videoTitle}
          </Text>
          <div className='plann-content-page-video flex-wrap xl:flex w-full'>
            <div className='w-[100%] max-w-[60%] flex-grow-[1.7]'>
              <VideoPlayer src={videoUrl} descriptionId='' descriptionBody='' />
            </div>
            <pre className='w-[40%] max-w-[40%] p-6 pr-6 font-sans text-sm whitespace-pre-wrap'>
              {videoDescription}
            </pre>
          </div>
        </>
      )}
    </>
  );
};
